import * as React from 'react';

import type * as api from '@gitbook/api-client';
import type { ContentRefKind } from '@gitbook/app-content-refs';
import { INLINE_MENTION } from '@gitbook/doc-core';
import { useHiveClient } from '@gitbook/hive-react';
import type { TextRange } from '@gitbook/slate';
import type { MarkProps } from '@gitbook/slate-react';

import {
    type CreateInputContentRefPaletteOptions,
    createInputContentRefPalette,
    useContentReferenceContext,
} from '../../ContentReferences';
import { SelectionPalettePicker } from '../common';
import { ContentEditorContext } from '../contexts';

/**
 * Component being rendered around the temporary mark created when typing "@" or "#".
 */
export function MentionPicker(
    props: MarkProps & {
        options: CreateInputContentRefPaletteOptions<ContentRefKind[]>;
    }
): React.ReactElement {
    const { editor, options } = props;

    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const contentReferenceContext = useContentReferenceContext();
    const hiveClient = useHiveClient();

    const getPalette = (_markData, getCurrentRange: () => TextRange) => {
        const replaceWithMention = (reference: api.ContentRef) => {
            editor.change((change) => {
                change
                    .insertInlineAtRange(getCurrentRange(), {
                        type: INLINE_MENTION,
                        data: { ref: reference },
                        isVoid: true,
                    })
                    .focus();
            });

            logAnalyticsEvent({
                type: 'edit_page_document_insert_inline',
                inlineType: 'mention',
                from: 'keyboard-shortcut',
                kind: reference.kind,
            });
        };

        return createInputContentRefPalette(
            replaceWithMention,
            {
                ...contentReferenceContext,
                document: editor.value.document,
            },
            hiveClient,
            {
                ...options,
                // The mention palette is using the text value typed in the editor for search
                search: { enabled: false },
            }
        );
    };

    return <SelectionPalettePicker {...props} getPalette={getPalette} />;
}
